import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminListPackages, adminSavePackage } from '../../api/packagesApi';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import PackageFormFields from './PackageFormFields';
import { defaultPackageFormData, mapPackageToFormData, normalizePackageLimits, slugFromPackageName } from './packageFormConstants';
import { PACKAGE_FEATURE_DRAG_TYPE } from './feature-library/collectPackageFeatures';
import { findPackageByRouteParam, getPackageUrlSegment } from './packageRouteUtils';
import { adminPackageEditPath } from '../../constants/adminPaths';
import { PageSkeleton } from '../../../components/loading/skeletons';
import '../PackageManagement.css';
import './package-form-page.css';

const PackageFormPage = () => {
  const navigate = useNavigate();
  const { packageId: packageRouteParam } = useParams();
  const isEdit = Boolean(packageRouteParam);

  const [formData, setFormData] = useState(defaultPackageFormData);
  const [allPackages, setAllPackages] = useState([]);
  const [savedPackageId, setSavedPackageId] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [draggedFeatureIndex, setDraggedFeatureIndex] = useState(null);
  const [dragOverFeatureIndex, setDragOverFeatureIndex] = useState(null);
  const { admin } = useAdminAuth();
  const isSuperAdmin = admin?.role === 'super-admin';

  useEffect(() => {
    const loadPackages = async () => {
      try {
        setAllPackages(await adminListPackages());
      } catch (err) {
        console.error('Error loading packages for feature library:', err);
      }
    };

    loadPackages();
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const loadPackage = async () => {
      try {
        setLoading(true);
        const packages = await adminListPackages();
        setAllPackages(packages);

        const pkg = findPackageByRouteParam(packages, packageRouteParam);

        if (!pkg) {
          setError('Package not found');
          return;
        }

        setSavedPackageId(pkg.id);
        setFormData(mapPackageToFormData(pkg));

        const canonicalSegment = getPackageUrlSegment(pkg);
        if (canonicalSegment && canonicalSegment !== packageRouteParam) {
          navigate(adminPackageEditPath(pkg), { replace: true });
        }
      } catch (err) {
        console.error('Error loading package:', err);
        setError('Failed to load package: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPackage();
  }, [isEdit, packageRouteParam, navigate]);

  const handleFeatureDragStart = (index, event) => {
    setDraggedFeatureIndex(index);
    setDragOverFeatureIndex(index);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  };

  const handleFeatureDragEnd = () => {
    setDraggedFeatureIndex(null);
    setDragOverFeatureIndex(null);
  };

  const appendFeatureIfNew = (featureText) => {
    const trimmed = String(featureText || '').trim();
    if (!trimmed) return false;

    setFormData((prev) => {
      const exists = prev.features.some(
        (feature) => String(feature || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) return prev;
      return { ...prev, features: [...prev.features, trimmed] };
    });

    return true;
  };

  const handleAddCustomFeature = () => {
    setFormData((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const handleLibraryFeatureDrop = (event, insertIndex = null) => {
    event.preventDefault();
    const featureText = event.dataTransfer.getData(PACKAGE_FEATURE_DRAG_TYPE)
      || event.dataTransfer.getData('text/plain');

    if (!featureText) return;

    const trimmed = featureText.trim();
    if (!trimmed) return;

    setFormData((prev) => {
      const exists = prev.features.some(
        (feature) => String(feature || '').trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) return prev;

      const updatedFeatures = [...prev.features];
      if (insertIndex === null || insertIndex >= updatedFeatures.length) {
        updatedFeatures.push(trimmed);
      } else {
        updatedFeatures.splice(insertIndex, 0, trimmed);
      }

      return { ...prev, features: updatedFeatures };
    });
  };

  const handleFeatureDragOver = (event, index) => {
    if (event.dataTransfer.types.includes(PACKAGE_FEATURE_DRAG_TYPE)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      return;
    }

    event.preventDefault();
    if (draggedFeatureIndex === null || draggedFeatureIndex === index) return;
    setDragOverFeatureIndex(index);
  };

  const handleFeatureDrop = (index) => {
    if (draggedFeatureIndex === null || draggedFeatureIndex === index) {
      setDragOverFeatureIndex(null);
      return;
    }

    setFormData((prev) => {
      const updatedFeatures = [...prev.features];
      const [moved] = updatedFeatures.splice(draggedFeatureIndex, 1);
      updatedFeatures.splice(index, 0, moved);
      return { ...prev, features: updatedFeatures };
    });

    setDraggedFeatureIndex(null);
    setDragOverFeatureIndex(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await adminSavePackage({
        packageId: isEdit ? savedPackageId : undefined,
        packageData: {
          ...formData,
          slug: String(formData.slug || '').trim() || slugFromPackageName(formData.name),
          limits: normalizePackageLimits(formData.limits),
          isTrialPackage: false,
          price: formData.isAdminPackage
            ? { monthly: 0, annually: 0 }
            : formData.price,
          subscription: {
            ...formData.subscription,
            trialDays: 0,
          },
        },
      });
      navigate(adminPath(ADMIN_PATHS.PACKAGES));
    } catch (err) {
      console.error('Error saving package:', err);
      setError('Failed to save package: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(adminPath(ADMIN_PATHS.PACKAGES));
  };

  if (loading) {
    return (
      <div className="admin-page pkg-form-page">
        <PageSkeleton variant="form" label="Loading package" />
      </div>
    );
  }

  return (
    <div className="admin-page pkg-form-page">
      <div className="pkg-form-page__header">
        <div>
          <button type="button" className="pkg-form-page__back" onClick={handleCancel}>
            ← Back to packages
          </button>
          <h1 className="pkg-form-page__title">
            {isEdit ? 'Edit Package' : 'Add New Package'}
          </h1>
          <p className="pkg-form-page__subtitle">
            {isEdit
              ? 'Update this package’s details, limits, and features.'
              : 'Create a catalog or internal admin package.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="pkg-alert pkg-alert-error">
          <span>⚠️</span>
          {error}
        </div>
      )}

      <PackageFormFields
        formData={formData}
        setFormData={setFormData}
        isSuperAdmin={isSuperAdmin}
        allPackages={allPackages}
        currentPackageId={isEdit ? savedPackageId : null}
        draggedFeatureIndex={draggedFeatureIndex}
        dragOverFeatureIndex={dragOverFeatureIndex}
        onFeatureDragStart={handleFeatureDragStart}
        onFeatureDragOver={handleFeatureDragOver}
        onFeatureDrop={handleFeatureDrop}
        onFeatureDragEnd={handleFeatureDragEnd}
        onLibraryFeatureDrop={handleLibraryFeatureDrop}
        onSelectLibraryFeature={appendFeatureIfNew}
        onAddCustomFeature={handleAddCustomFeature}
      />

      <div className="pkg-form-page__footer">
        <button
          type="button"
          className="pkg-btn pkg-btn-secondary"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="pkg-btn pkg-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : isEdit ? 'Update Package' : 'Create Package'}
        </button>
      </div>
    </div>
  );
};

export default PackageFormPage;
